import React from 'react';

const Pricing: React.FC = () => {
  const plans = [
    {
      name: "Starter",
      price: "$99",
      period: "/month",
      description: "Perfect for small businesses getting started with AI voice agents",
      features: [
        "Up to 1,000 conversations/month",
        "Basic AI voice agent",
        "Email support",
        "Standard integrations",
        "Basic analytics"
      ],
      popular: false
    },
    {
      name: "Professional",
      price: "$299",
      period: "/month", 
      description: "Ideal for growing companies with higher volume needs",
      features: [
        "Up to 10,000 conversations/month",
        "Advanced AI voice agent",
        "Priority support",
        "All integrations",
        "Advanced analytics",
        "Custom voice training",
        "Multi-language support"
      ],
      popular: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "Tailored solutions for large organizations",
      features: [
        "Unlimited conversations",
        "Enterprise AI voice agent",
        "Dedicated support team",
        "Custom integrations",
        "White-label solution",
        "Advanced security",
        "SLA guarantees",
        "On-premise deployment"
      ],
      popular: false
    }
  ];

  return (
    <section id="pricing" className="py-32 px-4 sm:px-6 lg:px-8 section-dark relative overflow-hidden">
      {/* Background Effects - Corporate Colors */}
      <div className="floating-orb w-96 h-96 bg-slate-700 top-20 right-20"></div>
      <div className="floating-orb w-80 h-80 bg-blue-600 bottom-40 left-10"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-6 py-3 rounded-full glass-effect text-white text-sm font-medium mb-8">
            <span className="gradient-text font-semibold">Enterprise Pricing</span>
          </div>
          
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8 leading-tight">
            Transparent, Scalable{' '}
            <span className="gradient-text">Pricing</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Choose the perfect plan for your business needs. All plans include our enterprise-grade AI voice technology 
            with transparent pricing, dedicated support, and flexible scaling options.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-32">
          {plans.map((plan, index) => (
            <div 
              key={index}
              className={`relative glass-card rounded-3xl p-8 card-hover ${
                plan.popular ? 'neon-border scale-105' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 mb-6">{plan.description}</p>
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-black gradient-text">{plan.price}</span>
                  <span className="text-gray-400 ml-1">{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center">
                    <svg className="w-5 h-5 text-blue-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <button 
                className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${
                  plan.popular 
                    ? 'btn-primary' 
                    : 'btn-secondary'
                }`}
              >
                {plan.name === 'Enterprise' ? 'Contact Sales' : 'Start Free Trial'}
              </button>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="glass-card rounded-3xl p-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
          
          <div className="relative z-10">
            <h3 className="text-4xl font-black text-center text-white mb-12">
              Frequently Asked <span className="gradient-text">Questions</span>
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="glass-effect rounded-2xl p-6">
                <h4 className="text-lg font-bold text-white mb-3">
                  Is there a free trial available?
                </h4>
                <p className="text-gray-300">
                  Yes! We offer a 14-day free trial for all plans. No credit card required to get started.
                </p>
              </div>
              <div className="glass-effect rounded-2xl p-6">
                <h4 className="text-lg font-bold text-white mb-3">
                  Can I change plans anytime?
                </h4>
                <p className="text-gray-300">
                  Absolutely. You can upgrade or downgrade your plan at any time. Changes take effect immediately.
                </p>
              </div>
              <div className="glass-effect rounded-2xl p-6">
                <h4 className="text-lg font-bold text-white mb-3">
                  What happens if I exceed my conversation limit?
                </h4>
                <p className="text-gray-300">
                  We'll notify you when you're approaching your limit. You can upgrade your plan or purchase additional conversations.
                </p>
              </div>
              <div className="glass-effect rounded-2xl p-6">
                <h4 className="text-lg font-bold text-white mb-3">
                  Do you offer custom enterprise solutions?
                </h4>
                <p className="text-gray-300">
                  Yes, our Enterprise plan includes custom solutions, dedicated support, and on-premise deployment options.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing; 